import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  PWA_INSTALL_SIZE_LABEL,
  usePwaInstall,
} from '@/lib/pwaInstall';

/**
 * Permanent install entry on Settings/Account.
 *
 * Mobile-only by design: desktop has install affordances built into
 * the browser chrome (URL bar icon, menu item), so duplicating it in
 * Settings just adds noise. Also hidden inside the PWA itself.
 *
 * No "installed" state — Chrome doesn't reliably expose whether the
 * site is already an installed PWA in the current browser (the
 * standalone matchMedia query only fires when the user is *launching
 * from* the installed icon). Showing a "yes, installed!" badge based
 * on partial signals would lie too often.
 */
export function InstallPwaCard() {
  const { t } = useT();
  const { platform, isStandalone, install } = usePwaInstall();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [fallbackHint, setFallbackHint] = React.useState(false);

  // Hide outside the use-the-PWA-on-your-phone scenarios:
  //   - Desktop: browser's own URL-bar install affordance is enough.
  //   - In-PWA: no point asking the user to install what they're using.
  //   - Browsers without any install path ("none"): button would do
  //     nothing useful.
  if (!isMobile || isStandalone || platform === 'none') return null;

  async function handleClick() {
    setFallbackHint(false);
    const outcome = await install();
    if (outcome === 'unavailable') {
      // Browser hasn't decided to surface beforeinstallprompt yet (or
      // it's iOS where there's no programmatic path). Show the menu
      // hint inline so the user has a next step.
      setFallbackHint(true);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('pwa.installCard.title')}</CardTitle>
        <CardDescription>
          {t('pwa.installCard.subtitle', { size: PWA_INSTALL_SIZE_LABEL })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button onClick={handleClick} className="gap-2">
          <Download className="size-4" />
          {t('pwa.install.cta')}
        </Button>
        {fallbackHint && (
          <p className="text-xs text-muted-foreground">
            {platform === 'ios'
              ? t('pwa.install.iosHowTo')
              : t('pwa.install.browserMenuHint')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
